import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import api, { getClients, createClient, deleteClient, logExport } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useAsk } from '../context/PromptProvider'
import { usePageShortcut } from '../hooks/useKeyboardShortcuts'
import { InlineText } from '../components/InlineEdit'
import { recentClientsTracker } from '../lib/recentItems'
import { SkeletonTable, LoadingRegion } from '../components/Skeleton'
import EmptyState from '../components/EmptyState'
import useTableSort, { SortIcon } from '../hooks/useTableSort'
import useBulkSelect from '../hooks/useBulkSelect'
import BulkActionBar from '../components/BulkActionBar'
import { useBulkUndo } from '../hooks/useBulkUndo'
import { downloadCSV } from '../lib/csv'
import useFieldIds from '../hooks/useFieldIds'
import HelpButton from '../components/HelpButton'
import { displayClientName, clientSortKey } from '../lib/displayClient'
import { firmDateIso } from '../utils/eventTime'

export default function Clients() {
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const { user } = useAuth()
  const toast = useToast()
  const ask = useAsk()
  const sort = useTableSort('clients', { key: 'last_name', dir: 'asc' })
  const [searchParams, setSearchParams] = useSearchParams()
  const [showForm, setShowForm] = useState(false)
  const [search, setSearch] = useState('')

  usePageShortcut('n', () => setShowForm(true))
  usePageShortcut('f', () => document.querySelector('input[placeholder*="Search clients"]')?.focus())
  const [form, setForm] = useState({
    // Feedback #474 (Shawn 2026-08-04): add Company client type + contact-
    // person fields for company clients.  Backend POST /api/clients already
    // accepts company_name + client_type; the create form just didn't expose
    // them.  ClientDetail's Edit form has had these for months — this brings
    // the create form to parity.
    client_type: 'individual',
    company_name: '',
    first_name: '', last_name: '', email: '', phone: '', address: '', notes: ''
  })
  const fid = useFieldIds()

  useEffect(() => { fetchClients() }, [])

  // ?new=1 from Ctrl+K "New client" → auto-open the form + clean the URL
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setShowForm(true)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const fetchClients = async () => {
    try {
      const res = await getClients()
      setClients(res.data)
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading clients')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      // Feedback #474: if client_type=company, first/last are the CONTACT
      // person at the company.  Backend accepts both — no coercion needed.
      // If company chosen but company_name blank, refuse client-side rather
      // than saving an unnamed company.
      if (form.client_type === 'company' && !form.company_name.trim()) {
        setError('Company Name is required when Client Type is Company.')
        return
      }
      await createClient(form)
      setForm({ client_type: 'individual', company_name: '', first_name: '', last_name: '', email: '', phone: '', address: '', notes: '' })
      setShowForm(false)
      setError('')
      fetchClients()
    } catch (err) {
      setError(err.response?.data?.error || 'Error creating client')
    }
  }

  const handleDelete = async (id) => {
    if (!await ask({ type: 'confirm', title: 'Delete client?', message: 'Delete this client? This will also delete all associated matters and time entries.', confirmLabel: 'Delete', danger: true })) return
    try {
      await deleteClient(id)
      recentClientsTracker.remove(id)   // drop from sidebar Recent list
      setError('')
      fetchClients()
    } catch (err) {
      setError(err.response?.data?.error || 'Error deleting client')
    }
  }

  const canEditClients = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_create_clients

  // Inline field save — sends the full client row + the changed field so the
  // non-COALESCE'd PUT doesn't null-out unrelated columns.
  const saveInlineField = async (row, field, value) => {
    try {
      const res = await api.put(`/api/clients/${row.id}`, { ...row, [field]: value || null })
      setClients((prev) => prev.map((c) => c.id === row.id ? { ...c, ...res.data } : c))
      toast.success('Client updated')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save — reverted')
      throw err
    }
  }

  // Compute visible list at component scope so useBulkSelect can hook it.
  // Feedback #506: search must also match company_name (entity/business clients)
  // and the display accessor for sort uses a display-name fallback so companies
  // sort alphabetically among individuals rather than clumping under empty
  // last_name.
  const filteredRaw = [...clients]
    .filter(c => {
      if (!search) return true
      const q = search.toLowerCase()
      return `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(q) ||
        (c.company_name || '').toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.includes(q)
    })
  const visible = sort.apply(filteredRaw, {
    last_name: clientSortKey,
    email: (c) => (c.email || '').toLowerCase(),
    phone: (c) => c.phone || '',
  }) || filteredRaw

  // Bulk selection on the visible rows.  Auto-prunes when search/sort
  // changes the visible id set.
  const bulk = useBulkSelect(visible, { rowKey: 'id' })
  const bulkUndo = useBulkUndo()

  // Bulk status change — hits the new /api/clients/bulk/status endpoint.
  // Snapshot per-id prior status BEFORE apply so Undo can restore.  Revert
  // loops per-id PUT since the bulk endpoint takes only one status per call
  // and prior statuses may vary across the selection.
  const handleBulkStatus = (newStatus) => {
    if (!canEditClients || bulk.size === 0) return
    const ids = Array.from(bulk.selectedIds)
    bulkUndo.run({
      ids,
      snapshot: (idList) => idList
        .map((id) => {
          const row = clients.find((c) => c.id === id)
          return row ? { id: row.id, status: row.status || 'active' } : null
        })
        .filter(Boolean),
      apply: () => api.put('/api/clients/bulk/status', { ids, status: newStatus }),
      revert: (snap) => Promise.all((snap || []).map((s) =>
        api.put(`/api/clients/${s.id}`, { status: s.status })
      )),
      label: `Marked ${ids.length} client${ids.length === 1 ? '' : 's'} ${newStatus}`,
      onAfter: () => {
        bulk.clear()
        fetchClients()
      },
    })
  }

  // Bulk export CSV — columns mirror the visible table (Name / Email / Phone)
  // plus id for downstream matching.  Uses the shared csv helper: Excel-safe
  // quoting + CSV-injection defense on leading =/+/-/@.
  const handleBulkExport = () => {
    if (bulk.size === 0) return
    const ids = bulk.selectedIds
    const rows = clients
      .filter((c) => ids.has(c.id))
      .map((c) => ({
        id: c.id,
        // displayClientName, as the table shows it: a company client has no
        // first/last name and exported with a blank Name (Amicus report
        // parity bug 7).
        name: displayClientName(c),
        email: c.email || '',
        phone: c.phone || '',
      }))
    const columns = [
      { key: 'id', label: 'ID' },
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
    ]
    // The firm's day, never the UTC one (a day ahead every Pacific evening).
    const filename = `clients-${firmDateIso()}.csv`
    try {
      downloadCSV(filename, rows, columns)
    } catch {
      toast.error('Export failed: the CSV file could not be saved')
      return
    }
    // Export audit only once the file is saved (CLAUDE.md "Exports on any
    // page"; resource_type registered in api/src/routes/exports.js).
    logExport({ resource_type: 'client_list', format: 'csv', filename, count: rows.length })
    toast.success(`Exported ${rows.length} client${rows.length === 1 ? '' : 's'}`)
  }

  if (loading) return (
    <LoadingRegion label="Loading clients">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="h-8 w-40 bg-gray-100 rounded animate-pulse mb-4" />
        <SkeletonTable rows={8} cols={4} />
      </div>
    </LoadingRegion>
  )

  return (
    <div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">{error}</div>}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Clients<HelpButton slug="clients" /></h2>
        {(user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.can_create_clients) && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            + New Client
          </button>
        )}
      </div>

      {/* New Client Form */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">New Client</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Feedback #474 (Shawn 2026-08-04): Client Type picker.
                Individual = a person; Company = a business entity, with a
                contact-person name captured in first/last so we know who to
                call/email at that company. */}
            <div className="md:col-span-2">
              <label htmlFor={fid('client-type')} className="block text-sm font-medium text-gray-700 mb-1">Client Type</label>
              <select id={fid('client-type')}
                value={form.client_type}
                onChange={e => setForm({...form, client_type: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="individual">Individual (a person)</option>
                <option value="company">Company (a business entity)</option>
              </select>
            </div>
            {form.client_type === 'company' && (
              <div className="md:col-span-2">
                <label htmlFor={fid('company-name')} className="block text-sm font-medium text-gray-700 mb-1">Company Name <span className="text-red-500">*</span></label>
                <input id={fid('company-name')}
                  type="text"
                  required
                  value={form.company_name}
                  onChange={e => setForm({...form, company_name: e.target.value})}
                  placeholder="Legal name of the entity (e.g. Acme Corp., Smith & Jones LLC)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 First / Last below capture your <strong>contact person</strong> at this company — who to call or email.
                </p>
              </div>
            )}
            <div>
              <label htmlFor={fid('first-name')} className="block text-sm font-medium text-gray-700 mb-1">
                {form.client_type === 'company' ? 'Contact — First Name' : 'First Name'}
              </label>
              <input id={fid('first-name')}
                type="text"
                required
                value={form.first_name}
                onChange={e => setForm({...form, first_name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('last-name')} className="block text-sm font-medium text-gray-700 mb-1">
                {form.client_type === 'company' ? 'Contact — Last Name' : 'Last Name'}
              </label>
              <input id={fid('last-name')}
                type="text"
                required
                value={form.last_name}
                onChange={e => setForm({...form, last_name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('email')} className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input id={fid('email')}
                type="email"
                value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor={fid('phone')} className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input id={fid('phone')}
                type="text"
                value={form.phone}
                onChange={e => setForm({...form, phone: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor={fid('address')} className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input id={fid('address')}
                type="text"
                value={form.address}
                onChange={e => setForm({...form, address: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor={fid('notes')} className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea id={fid('notes')}
                value={form.notes}
                onChange={e => setForm({...form, notes: e.target.value})}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button type="submit" className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                Save Client
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search — Feedback #506: also matches company_name; helper hint reminds
          users of Ctrl+F shortcut.  Alphabetical sort applied by default via
          useTableSort('clients', {key:'last_name', dir:'asc'}). */}
      <div className="mb-4">
        <input type="text" placeholder="Search clients — start typing name, company, email, or phone (Ctrl+F)"
          value={search} onChange={e => setSearch(e.target.value)}
          autoFocus
          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>

      {/* Bulk-action bar — renders only when at least one row is selected.
          Bulk delete deliberately omitted: single-client delete cascades to
          matters + time_entries and needs deliberate per-row confirmation. */}
      <BulkActionBar
        count={bulk.size}
        onClear={bulk.clear}
        tone="primary"
        actions={[
          ...(canEditClients ? [
            { label: 'Mark Active', icon: '✓', onClick: () => handleBulkStatus('active') },
            { label: 'Mark Inactive', icon: '⏸️', onClick: () => handleBulkStatus('inactive') },
            { label: 'Mark Prospect', icon: '🌱', onClick: () => handleBulkStatus('prospect') },
          ] : []),
          { label: 'Export CSV', icon: '⬇️', onClick: handleBulkExport },
        ]}
      />

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {clients.length === 0 ? (
          <EmptyState
            icon="👥"
            title="No clients yet"
            description="Clients are the people or organizations you represent.  Add your first one to start creating matters, tracking time, and generating invoices."
            action={canEditClients ? { label: '+ New Client', onClick: () => setShowForm(true) } : undefined}
          />
        ) : visible.length === 0 ? (
          <EmptyState
            icon="👥"
            variant="filtered"
            title="No clients match your search"
            description={`No clients found matching "${search}". Try clearing the search box or checking your spelling.`}
            action={{ label: 'Clear search', onClick: () => setSearch('') }}
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all visible clients"
                    checked={bulk.isAllSelected}
                    onChange={(e) => bulk.toggleAll(visible, e.target.checked)}
                  />
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('last_name')}>Name <SortIcon dir={sort.key === 'last_name' ? sort.dir : null} /></th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('email')}>Email <SortIcon dir={sort.key === 'email' ? sort.dir : null} /></th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('phone')}>Phone <SortIcon dir={sort.key === 'phone' ? sort.dir : null} /></th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map(client => (
                <tr key={client.id} className="hover:bg-gray-50">
                  <td className="px-3 py-4 w-8">
                    <input
                      type="checkbox"
                      aria-label={`Select client ${displayClientName(client)}`}
                      checked={bulk.has(client.id)}
                      onChange={() => bulk.toggle(client.id)}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <Link to={`/clients/${client.id}`} className="font-medium text-primary-600 hover:underline">
                      {displayClientName(client)}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    <InlineText
                      value={client.email}
                      canEdit={canEditClients}
                      placeholder="(no email)"
                      onSave={(v) => saveInlineField(client, 'email', v)}
                    />
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    <InlineText
                      value={client.phone}
                      canEdit={canEditClients}
                      placeholder="(no phone)"
                      onSave={(v) => saveInlineField(client, 'phone', v)}
                    />
                  </td>
                  <td className="px-6 py-4">
                    {((user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_delete_clients) && (
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  )
}
